import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import type {
  PerformancePresetRange,
  PerformanceUrlSearch,
} from './performance.types'
import { usePerformanceOptions } from './performanceQueries'
import { resolvePerformanceUrl } from './performanceUrl'
import { appendTargetMarketIds } from './targetMarkets'
import type { PerformanceSelectionState } from './usePerformanceSelection.types'

// Every action mutates a URL draft and re-resolves it, so pushed params are always normalized.
export function usePerformanceSelection(): PerformanceSelectionState {
  const [searchParams, setSearchParams] = useSearchParams()
  const optionsQuery = usePerformanceOptions()
  const options = optionsQuery.data

  const resolved = useMemo(() => {
    if (!options) return null

    return resolvePerformanceUrl(options, searchParams, new Date())
  }, [options, searchParams])

  // Deterministic recovery for an invalid initial/deep-linked URL; replaces rather than pushes.
  useEffect(() => {
    if (!resolved) return
    if (resolved.normalizedParams.toString() !== searchParams.toString()) {
      setSearchParams(resolved.normalizedParams, { replace: true })
    }
  }, [resolved, searchParams, setSearchParams])

  const pushSelection = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      if (!options) return

      const draft = new URLSearchParams(searchParams)
      mutate(draft)

      const next = resolvePerformanceUrl(options, draft, new Date())

      setSearchParams(next.normalizedParams)
    },
    [options, searchParams, setSearchParams]
  )
  const onBrandChange = useCallback(
    (brandId: string) =>
      pushSelection((params) => {
        params.set('brand', brandId)
        params.delete('category')
        params.delete('market')
        params.delete('targetMarketIds')
        params.delete('search')
      }),
    [pushSelection]
  )
  const onCategoryChange = useCallback(
    (category: string) =>
      pushSelection((params) => {
        params.set('category', category)
        params.delete('market')
        params.delete('targetMarketIds')
        params.delete('search')
      }),
    [pushSelection]
  )
  const onMarketChange = useCallback(
    (market: string) =>
      pushSelection((params) => {
        params.set('market', market)
        params.delete('targetMarketIds')
        params.delete('search')
      }),
    [pushSelection]
  )
  const onTargetMarketChange = useCallback(
    (targetMarketIds: string[]) =>
      pushSelection((params) => {
        params.delete('market')
        params.delete('targetMarketIds')

        appendTargetMarketIds(params, targetMarketIds)

        params.delete('search')
      }),
    [pushSelection]
  )
  const onSearchChange = useCallback(
    (search: PerformanceUrlSearch) =>
      pushSelection((params) => {
        params.set('search', search)
      }),
    [pushSelection]
  )

  const onRangeChange = useCallback(
    (range: PerformancePresetRange) =>
      pushSelection((params) => {
        params.set('range', range)
        params.delete('from')
        params.delete('to')
      }),
    [pushSelection]
  )

  const onApplyCustom = useCallback(
    (custom: { from: string; to: string }) =>
      pushSelection((params) => {
        params.set('range', 'custom')
        params.set('from', custom.from)
        params.set('to', custom.to)
      }),
    [pushSelection]
  )

  const brand = useMemo(
    () =>
      options?.brands.find(
        (candidate) => candidate.value === resolved?.selection?.brandId
      ),
    [options, resolved]
  )
  const category = useMemo(
    () =>
      brand?.categories.find(
        (candidate) => candidate.value === resolved?.selection?.category
      ),
    [brand, resolved]
  )

  return {
    isLoading: optionsQuery.isLoading,
    isError: optionsQuery.isError,
    options,
    selection: resolved?.selection ?? null,
    filters: resolved?.filters ?? null,
    brands: options?.brands ?? [],
    categories: brand?.categories ?? [],
    markets: category?.markets ?? [],
    targetMarkets: category?.targetMarkets ?? [],
    onBrandChange,
    onCategoryChange,
    onMarketChange,
    onTargetMarketChange,
    onSearchChange,
    onRangeChange,
    onApplyCustom,
  }
}
